/**
 * faseGiornate — in che punto del turno siamo, visto dalla home.
 *
 * Andrea 2026-09-11: il ciclo della home deve essere questo.
 *   1. Prima del turno: "Prossima sfida" = la partita di questa giornata,
 *      col countdown per consegnare la formazione.
 *   2. Turno in corso (dalla deadline, cioè 15' prima del primo fischio
 *      di Serie A, fino al calcolo): la partita di questa giornata resta
 *      in un blocco "in corso" col bottone Vai al live, e "Prossima sfida"
 *      passa alla giornata dopo col suo countdown.
 *   3. Turno calcolato: resta il risultato (Incontro concluso) e la
 *      prossima sfida col timer, finché non parte il turno successivo.
 *
 * Funzione pura: prende la lista giornate e "adesso", restituisce quale
 * giornata è in corso, quale è la prossima da schierare e l'ultima
 * calcolata. Così si testa senza montare la pagina.
 */

export interface GiornataFase {
  id: string;
  numero: number;
  numero_serie_a: number;
  stato: string;
  /** Primo fischio di Serie A della giornata (dal calendario SA). */
  data_inizio_partite_sa: string | null;
}

/** Stessa tolleranza del countdown: la formazione si blocca 15' prima. */
export const GRACE_MINUTI = 15;

/** Giornate non ancora chiuse dal calcolo: la prima di queste è "la
 * giornata corrente", che sia ancora da giocare o coi voti in arrivo. */
const STATI_APERTI = new Set(["da_giocare", "in_corso", "voti_caricati", "voti_da_confermare"]);

export interface FaseGiornate<G extends GiornataFase> {
  /** Giornata col turno iniziato e non ancora calcolata. */
  inCorso: G | null;
  /** Giornata per cui si schiera: la corrente se il turno non è partito,
   * altrimenti la successiva ancora da giocare. */
  prossima: G | null;
  /** Ultima giornata calcolata (numero più alto). */
  ultimaCalcolata: G | null;
}

export function faseGiornate<G extends GiornataFase>(
  giornate: G[] | undefined,
  adessoMs: number,
): FaseGiornate<G> {
  const ordinate = [...(giornate ?? [])].sort((a, b) => a.numero - b.numero);
  const ultimaCalcolata =
    [...ordinate].reverse().find((g) => g.stato === "calcolata") ?? null;
  const aperte = ordinate.filter((g) => STATI_APERTI.has(g.stato));
  const corrente = aperte[0] ?? null;
  if (!corrente) return { inCorso: null, prossima: null, ultimaCalcolata };

  const inizioMs = corrente.data_inizio_partite_sa
    ? new Date(corrente.data_inizio_partite_sa).getTime()
    : null;
  // Voti caricati o giornata segnata in corso: il turno è partito per
  // forza. Altrimenti guardiamo l'orologio contro la deadline.
  const turnoPartito =
    corrente.stato !== "da_giocare" ||
    (inizioMs !== null && adessoMs >= inizioMs - GRACE_MINUTI * 60_000);
  if (!turnoPartito) return { inCorso: null, prossima: corrente, ultimaCalcolata };

  const prossima =
    aperte.find((g) => g.numero > corrente.numero && g.stato === "da_giocare") ?? null;
  return { inCorso: corrente, prossima, ultimaCalcolata };
}
